import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { EditorState, convertFromRaw, convertToRaw } from "draft-js";
import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import { BackButton, SaveButton } from "../../components/common/MainButtonComponent";
import DataHandler from "../../handlers/DataHandler";
import { routes } from "../../helper/routes";
import { questionTypeConstants } from "../../helper/constants";
import { updateQuestionById } from "../../service/assessSections/assessSectionsService/assessSectionsService";
import { RichQuestionEditor } from "./QuestionEditorForm";
import PassageModal from "./PassageModal";
import { passageService } from "../../service/question/passageService";
import { passageTypeService } from "../../service/question/passageTypeService";
import { AzureStorage } from "../../helper/azureStorage";
import config from "../../helper/config";

function parseRawList(value) {
  try {
    if (!value) return [];

    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
}

function rawToEditorState(raw) {
  try {
    if (!raw || !raw.blocks) return EditorState.createEmpty();

    return EditorState.createWithContent(convertFromRaw(raw));
  } catch {
    return EditorState.createEmpty();
  }
}

function editorStateIsEmpty(editorState) {
  if (!editorState) return true;

  const raw = convertToRaw(editorState.getCurrentContent());
  const hasText = raw.blocks.some((block) => block.text.trim() !== "");
  const hasEntity = Object.keys(raw.entityMap || {}).length > 0;

  return !hasText && !hasEntity;
}

function showAlert({ title, text, icon }) {
  if (window.Swal) {
    window.Swal.fire({ title, text, icon });
    return;
  }

  alert(text);
}

function isMcqQuestion(question) {
  return String(question?.qtype) === "1" || String(question?.qtype).toLowerCase() === "mcq";
}

function isSubmissionQuestion(question) {
  return (
    String(question?.qtype) === String(questionTypeConstants.SUBMISSION.id) ||
    String(question?.qtype).toLowerCase() === "submission"
  );
}

function isSubQuestionType(question) {
  return (
    String(question?.qtype) === String(questionTypeConstants.SUBQUESTION.id) ||
    String(question?.qtype).toLowerCase() === "sub question" ||
    String(question?.qtype).toLowerCase() === "subquestion"
  );
}

function parseAttachmentAnswer(answer) {
  try {
    const parsed = typeof answer === "string" ? JSON.parse(answer) : answer;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getQuestionTypeLabel(qtype) {
  const normalizedQtype = String(qtype);

  if (normalizedQtype === String(questionTypeConstants.MCQ.id)) {
    return questionTypeConstants.MCQ.name;
  }

  if (normalizedQtype === String(questionTypeConstants.DESCRIPTIVE.id)) {
    return questionTypeConstants.DESCRIPTIVE.name;
  }

  if (normalizedQtype === String(questionTypeConstants.SUBMISSION.id)) {
    return questionTypeConstants.SUBMISSION.name;
  }

  if (normalizedQtype === String(questionTypeConstants.SUBQUESTION.id)) {
    return questionTypeConstants.SUBQUESTION.name;
  }

  return normalizedQtype || "Unknown";
}

function normalizeSubQuestion(subQuestion, index) {
  const contentList = parseRawList(
    subQuestion?.content ?? subQuestion?.question_content ?? subQuestion?.tempQuestion
  );
  const answerList = parseRawList(
    subQuestion?.answer ?? subQuestion?.answer_content ?? subQuestion?.tempAnswer
  );

  return {
    id: subQuestion?.question_id ?? subQuestion?.questionId ?? subQuestion?.id ?? index,
    questionId: subQuestion?.question_id ?? subQuestion?.questionId ?? subQuestion?.id ?? null,
    contentEditorState: rawToEditorState(contentList[0]),
    answerEditorState: rawToEditorState(answerList[0]),
    markAllocation: Number(subQuestion?.mark_allocation ?? subQuestion?.markAllocation ?? subQuestion?.sub_mark_allocation ?? 0) || 0,
  };
}

function buildRawArray(editorState) {
  return [convertToRaw(editorState.getCurrentContent())];
}
function getEditorRawString(editorState) {
  try {
    return JSON.stringify(convertToRaw(editorState.getCurrentContent()));
  } catch {
    return "";
  }
}

function normalizeFileList(files = []) {
  return files
    .map((file) => String(file).replace(/['"]+/g, "").trim())
    .filter(Boolean)
    .sort()
    .join("|");
}

function buildChoiceMarkDistribution(currentMarks = [], index) {
  const nextSelections = currentMarks.map((mark) => Number(mark) > 0);
  nextSelections[index] = !nextSelections[index];

  if (!nextSelections.some(Boolean)) {
    return currentMarks.map(() => 0);
  }

  const selectedIndexes = nextSelections.reduce((acc, isSelected, currentIndex) => {
    if (isSelected) acc.push(currentIndex);
    return acc;
  }, []);

  const baseMark = Math.floor(100 / selectedIndexes.length);
  let remainder = 100 - baseMark * selectedIndexes.length;

  return currentMarks.map((_, currentIndex) => {
    if (!nextSelections[currentIndex]) return 0;

    const nextMark = baseMark + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
    return nextMark;
  });
}
export default function PaperReviewQuestionEditPage({
  assessmentId,
  questionId,
  question,
  onSave,
  onBack,
}) {
  const history = useHistory();
  const [saving, setSaving] = useState(false);
  const [passageEditorState, setPassageEditorState] = useState(EditorState.createEmpty());
  const [selectedPassageDetails, setSelectedPassageDetails] = useState(null);
  const [selectedPassageLoading, setSelectedPassageLoading] = useState(false);
  const [selectedPassageError, setSelectedPassageError] = useState("");
  const [passageModalOpen, setPassageModalOpen] = useState(false);
  const [passageTypes, setPassageTypes] = useState([]);
  const [passages, setPassages] = useState([]);

  const [selectedPassageTypeId, setSelectedPassageTypeId] = useState("");
  const [selectedPassageId, setSelectedPassageId] = useState("");
  const [stemEditorState, setStemEditorState] = useState(
    EditorState.createEmpty()
  );
  const [guidanceEditorState, setGuidanceEditorState] = useState(EditorState.createEmpty());
  const [optionEditorStates, setOptionEditorStates] = useState([EditorState.createEmpty()]);
  const [optionMarks, setOptionMarks] = useState(["0"]);
  // const [answerValue, setAnswerValue] = useState("");
  const [answerEditorState, setAnswerEditorState] = useState(
    EditorState.createEmpty()
  );
  const [markAllocation, setMarkAllocation] = useState(0);
  const [subQuestions, setSubQuestions] = useState([]);
  const submissionFileInputRef = useRef(null);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  const questionTypeLabel = useMemo(() => getQuestionTypeLabel(question?.qtype), [question]);

  const submission = isSubmissionQuestion(question);
  const mcq = isMcqQuestion(question);
  const subQuestionType = isSubQuestionType(question);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [uploadingAnswerFile, setUploadingAnswerFile] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);
  const [pendingDeleteFiles, setPendingDeleteFiles] = useState([]);

  const fileToArrayBuffer = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  const handleFileDownload = async (fileName) => {
    try {
      const containerName = config.storageBlobConfigs.containerName;

      const cleanFileName = String(fileName)
        .replace(/['"]+/g, "")
        .trim();

      await AzureStorage.downloadBlob(containerName, cleanFileName);
    } catch (error) {
      console.error("File download failed", error);

      showAlert({
        title: "Failed!",
        text: "File download failed. Please try again.",
        icon: "error",
      });
    }
  };

  const loadPassageDropdownData = async () => {
    try {
      const [typeResult, passageResult] = await Promise.all([
        passageTypeService.getAllPassageTypes(),
        passageService.getPassageTitle(),
      ]);

      if (!typeResult?.success) {
        throw new Error(
          typeResult?.message || "Failed to load passage types."
        );
      }

      if (!passageResult?.success) {
        throw new Error(
          passageResult?.message || "Failed to load passages."
        );
      }

      setPassageTypes(typeResult.data?.passage_types || []);
      setPassages(passageResult.data?.passages || []);
    } catch (error) {
      console.error("Failed to load passage dropdown data:", error);

      setSelectedPassageError(
        error?.message || "Failed to load passage details."
      );
    }
  };

  const loadSelectedPassage = async (passageId) => {
    if (!passageId) {
      setSelectedPassageDetails(null);
      setPassageEditorState(EditorState.createEmpty());
      setSelectedPassageError("");
      return;
    }

    try {
      setSelectedPassageLoading(true);
      setSelectedPassageError("");

      const result = await passageService.getPassageById(passageId);

      if (!result?.success) {
        throw new Error(result?.message || "Failed to load passage.");
      }

      const passage = result.data?.passage || null;
      console.log("Passage details:", passage);

      setSelectedPassageDetails(passage);
      setSelectedPassageTypeId(
        passage?.passage_type_id
          ? String(passage.passage_type_id)
          : ""
      );

      setSelectedPassageId(
        passage?.passage_id
          ? String(passage.passage_id)
          : ""
      );

      const passageContentList = parseRawList(passage?.content);

      setPassageEditorState(
        rawToEditorState(passageContentList[0])
      );
    } catch (error) {
      console.error("Failed to load passage:", error);

      setSelectedPassageDetails(null);
      setPassageEditorState(EditorState.createEmpty());
      setSelectedPassageError(
        error?.message || "Failed to load passage."
      );
    } finally {
      setSelectedPassageLoading(false);
    }
  };

  useEffect(() => {
    if (!question) return;

    loadPassageDropdownData();

    if (question.passage_id) {
      loadSelectedPassage(question.passage_id);
    } else {
      setSelectedPassageDetails(null);
      setPassageEditorState(EditorState.createEmpty());
      setSelectedPassageError("");
    }

    const contentList = parseRawList(question.content);
    const guidanceList = parseRawList(question.guidance ?? question.guidence);
    const answerList =
      isSubmissionQuestion(question) ||
        isMcqQuestion(question) ||
        isSubQuestionType(question)
        ? []
        : parseRawList(question.answer);
    const mcq = isMcqQuestion(question);
    const questionOptions = mcq ? contentList.slice(1) : [];
    const initialAttachments = isSubmissionQuestion(question)
      ? parseAttachmentAnswer(question.answer)
      : [];

    setInitialSnapshot({
      stem: JSON.stringify(contentList[0] || {}),
      guidance: JSON.stringify(guidanceList[0] || {}),
      answer: String(question.answer ?? ""),
      marks: String(Number(question.mark_allocation ?? question.markAllocation ?? 0) || 0),
      attachments: normalizeFileList(initialAttachments),
    });

    const answerMarks = String(question.answer ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value !== "");
    const optionCount = mcq ? Math.max(questionOptions.length, 1) : 0;

    setStemEditorState(rawToEditorState(contentList[0]));
    setGuidanceEditorState(rawToEditorState(guidanceList[0]));
    setOptionEditorStates(
      mcq
        ? (questionOptions.length ? questionOptions : [null]).map((raw) => rawToEditorState(raw))
        : [EditorState.createEmpty()]
    );
    setOptionMarks(
      mcq
        ? Array.from({ length: optionCount }, (_, index) => answerMarks[index] ?? "0")
        : ["0"]
    );
    setAnswerEditorState(rawToEditorState(answerList[0]));
    setAttachmentFiles(
      isSubmissionQuestion(question)
        ? parseAttachmentAnswer(question.answer)
        : []
    );
    setPendingUploadFiles([]);
    setPendingDeleteFiles([]);
    setMarkAllocation(Number(question.mark_allocation ?? question.markAllocation ?? 0) || 0);
    setSubQuestions(
      Array.isArray(question.subquestions)
        ? question.subquestions.map(normalizeSubQuestion)
        : Array.isArray(question.sub_questions)
          ? question.sub_questions.map(normalizeSubQuestion)
          : Array.isArray(question.sub_questions_details)
            ? question.sub_questions_details.map(normalizeSubQuestion)
            : []
    );
  }, [question]);

  const hasPendingSubmissionChanges =
    pendingUploadFiles.length > 0 || pendingDeleteFiles.length > 0;

  const hasUnsavedChanges = useMemo(() => {
    if (!initialSnapshot) return false;

    const currentStem = getEditorRawString(stemEditorState);
    const currentGuidance = getEditorRawString(guidanceEditorState);
    const currentMarks = String(Number(markAllocation) || 0);

    const currentAnswer = mcq
      ? optionMarks.join(",")
      : submission
        ? JSON.stringify(attachmentFiles)
        : subQuestionType
          ? String(question?.answer ?? "")
          : JSON.stringify([
            convertToRaw(answerEditorState.getCurrentContent()),
          ]);

    const currentAttachments = normalizeFileList(attachmentFiles);

    return (
      currentStem !== initialSnapshot.stem ||
      currentGuidance !== initialSnapshot.guidance ||
      String(currentAnswer ?? "") !== String(initialSnapshot.answer ?? "") ||
      currentMarks !== initialSnapshot.marks ||
      currentAttachments !== initialSnapshot.attachments ||
      hasPendingSubmissionChanges
    );
  }, [
    initialSnapshot,
    stemEditorState,
    guidanceEditorState,
    markAllocation,
    mcq,
    optionMarks,
    submission,
    subQuestionType,
    question,
    attachmentFiles,
    answerEditorState,
    hasPendingSubmissionChanges,
  ]);

  const handleBack = async () => {
    if (hasUnsavedChanges) {
      const confirmed = window.Swal
        ? await window.Swal.fire({
          title: "Discard changes?",
          text: "You have unsaved changes. If you go back now, your changes will be lost.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Discard",
          cancelButtonText: "Stay",
        })
        : { isConfirmed: window.confirm("You have unsaved changes. Discard them?") };

      if (!confirmed.isConfirmed) return;
    }

    if (typeof onBack === "function") {
      onBack();
      return;
    }

    history.push(routes.ppr_rew_view.replace(":id", assessmentId));
  };

  const updateOptionEditor = (index, nextEditorState) => {
    setOptionEditorStates((prev) => {
      const next = [...prev];
      next[index] = nextEditorState;
      return next;
    });
  };

  const updateOptionMark = (index) => {
    setOptionMarks((prev) => buildChoiceMarkDistribution(prev, index));
  };

  const addOption = () => {
    setOptionEditorStates((prev) => [...prev, EditorState.createEmpty()]);
    setOptionMarks((prev) => [...prev, "0"]);
  };

  const removeOption = (index) => {
    setOptionEditorStates((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, optionIndex) => optionIndex !== index);
    });

    setOptionMarks((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, optionIndex) => optionIndex !== index);
    });
  };

  const updateSubQuestionField = (index, field, value) => {
    setSubQuestions((prev) =>
      prev.map((subQuestion, subIndex) =>
        subIndex === index ? { ...subQuestion, [field]: value } : subQuestion
      )
    );
  };

  const handleSubmissionFileUpload = (event) => {
    const input = event.target;
    const files = Array.from(input.files || []);

    if (!files.length) return;

    const nextFileNames = files.map((file) =>
      file.name.replace(/['"]+/g, "").trim()
    );

    setPendingUploadFiles((prev) => {
      const existing = new Set(prev.map((file) => file.name));
      return [
        ...prev,
        ...files.filter((file) => !existing.has(file.name)),
      ];
    });

    setAttachmentFiles((prev) =>
      Array.from(new Set([...prev, ...nextFileNames]))
    );

    setPendingDeleteFiles((prev) =>
      prev.filter((fileName) => !nextFileNames.includes(fileName))
    );

    input.value = "";
  };

  const handleSubmissionFileRemove = async (fileName) => {
    const confirmed = window.Swal
      ? await window.Swal.fire({
        title: "Remove file?",
        text: `${fileName} will be removed when you save changes.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, remove",
        cancelButtonText: "Cancel",
      })
      : { isConfirmed: window.confirm(`Remove ${fileName}?`) };

    if (!confirmed.isConfirmed) return;

    const cleanFileName = String(fileName).replace(/['"]+/g, "").trim();

    setAttachmentFiles((prev) =>
      prev.filter(
        (item) => String(item).replace(/['"]+/g, "").trim() !== cleanFileName
      )
    );

    setPendingUploadFiles((prev) =>
      prev.filter(
        (file) => file.name.replace(/['"]+/g, "").trim() !== cleanFileName
      )
    );

    const existedBefore = parseAttachmentAnswer(question?.answer).some(
      (item) => String(item).replace(/['"]+/g, "").trim() === cleanFileName
    );

    if (existedBefore) {
      setPendingDeleteFiles((prev) =>
        prev.includes(cleanFileName) ? prev : [...prev, cleanFileName]
      );
    }
  };

  const uploadPendingSubmissionFiles = async () => {
    if (!pendingUploadFiles.length) return;

    const containerName = config.storageBlobConfigs.containerName;

    for (const file of pendingUploadFiles) {
      const cleanFileName = file.name.replace(/['"]+/g, "").trim();
      const fileBuffer = await fileToArrayBuffer(file);

      await AzureStorage.uploadBlob(containerName, cleanFileName, fileBuffer);
    }
  };

  const deletePendingSubmissionFiles = async () => {
    if (!pendingDeleteFiles.length) return;

    const containerName = config.storageBlobConfigs.containerName;

    for (const fileName of pendingDeleteFiles) {
      const cleanFileName = String(fileName).replace(/['"]+/g, "").trim();
      await AzureStorage.deleteblob(containerName, cleanFileName);
    }
  };

  const handleSave = async () => {
    if (!question) {
      showAlert({
        title: "Failed!",
        text: "Question data is missing.",
        icon: "error",
      });
      return;
    }

    if (!assessmentId || !questionId) {
      showAlert({
        title: "Failed!",
        text: "Assessment or question id is missing.",
        icon: "error",
      });
      return;
    }

    if (editorStateIsEmpty(stemEditorState)) {
      showAlert({
        title: "Failed!",
        text: "Question content is required.",
        icon: "error",
      });
      return;
    }

    const mcq = isMcqQuestion(question);

    if (mcq) {
      const hasEmptyOption = optionEditorStates.some((editorState) => editorStateIsEmpty(editorState));

      if (hasEmptyOption) {
        showAlert({
          title: "Failed!",
          text: "All options are required.",
          icon: "error",
        });
        return;
      }

      const selectedOptionCount = optionMarks.filter((value) => Number(value) > 0).length;

      if (selectedOptionCount === 0) {
        showAlert({
          title: "Failed!",
          text: "Please select at least one correct answer.",
          icon: "error",
        });
        return;
      }
    }

    if (submission) {
      if (!attachmentFiles.length) {
        showAlert({
          title: "Failed!",
          text: "Model answer file is required.",
          icon: "error",
        });
        return;
      }

      if (uploadingAnswerFile) {
        showAlert({
          title: "Please wait!",
          text: "File upload is still in progress.",
          icon: "info",
        });
        return;
      }
    }

    // if (Number(markAllocation) <= 0) {
    //   showAlert({
    //     title: "Failed!",
    //     text: "Allocated marks must be greater than 0.",
    //     icon: "error",
    //   });
    //   return;
    // }

    const hasInvalidSubQuestion = subQuestions.some((subQuestion) => {
      const isEmptyContent = editorStateIsEmpty(subQuestion.contentEditorState);
      const isEmptyAnswer = editorStateIsEmpty(subQuestion.answerEditorState);
      // const invalidMarks = Number(subQuestion.markAllocation) <= 0;
      return isEmptyContent || isEmptyAnswer;
    });

    if (hasInvalidSubQuestion) {
      showAlert({
        title: "Failed!",
        text: "Each sub-question needs content and Model Answer.",
        icon: "error",
      });
      return;
    }

    const serializedSubQuestions = subQuestions.length
      ? subQuestions.map((subQuestion) => ({
        question_id: subQuestion.questionId,
        content: JSON.stringify(buildRawArray(subQuestion.contentEditorState)),
        answer: JSON.stringify(buildRawArray(subQuestion.answerEditorState)),
        mark_allocation: Number(subQuestion.markAllocation) || 0,
      }))
      : null;

    const payload = {
      answer: mcq
        ? optionMarks.join(",")
        : submission
          ? JSON.stringify(attachmentFiles)
          : subQuestionType
            ? question?.answer ?? null
            : JSON.stringify([
              convertToRaw(answerEditorState.getCurrentContent()),
            ]),
      content: JSON.stringify(
        mcq
          ? [
            convertToRaw(stemEditorState.getCurrentContent()),
            ...optionEditorStates.map((editorState) =>
              convertToRaw(editorState.getCurrentContent())
            ),
          ]
          : [convertToRaw(stemEditorState.getCurrentContent())]
      ),
      guidence: JSON.stringify([
        convertToRaw(guidanceEditorState.getCurrentContent()),
      ]),
      mark_allocation: Number(markAllocation) || 0,
      qtype: Number(question.qtype) || question.qtype,
      subQuestions: serializedSubQuestions,

      is_passage: Boolean(selectedPassageId),
      passage_id: selectedPassageId
        ? Number(selectedPassageId)
        : null,
    };

    setSaving(true);

    try {
      if (submission) {
        setUploadingAnswerFile(true);
        await uploadPendingSubmissionFiles();
        await deletePendingSubmissionFiles();
        setUploadingAnswerFile(false);
      }
      const result = await updateQuestionById(
        assessmentId,
        questionId,
        DataHandler.getFromSession("user_id"),
        payload
      );

      if (!result?.success) {
        throw new Error(result?.message || "Failed to update question");
      }

      if (window.Swal) {
        await window.Swal.fire({
          title: "Success!",
          text: "Question updated successfully.",
          icon: "success",
        });
      }

      if (typeof onSave === "function") {
        setPendingUploadFiles([]);
        setPendingDeleteFiles([]);
        await onSave({
          ...result,
          updatedQuestion: {
            ...question,
            answer: submission ? JSON.stringify(attachmentFiles) : payload.answer,
            content: payload.content,
            guidance: payload.guidence,
            guidence: payload.guidence,
            mark_allocation: payload.mark_allocation,
            is_passage: Boolean(selectedPassageId),passage_id: selectedPassageId? Number(selectedPassageId): null,
          },
        });
        return;
      }

      handleBack();
    } catch (error) {
      showAlert({
        title: "Failed!",
        text: error?.message || "Failed to update question.",
        icon: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ContentWrapper>
      <style>{`
        @media (min-width: 992px) and (max-width: 1024px) {
          .paper-question-edit-page .RichEditor-root-createquestion {
            width: 100%;
          }
        }
      `}</style>
      <section className="question-create-page paper-question-edit-page">
        <div className="question-create-topbar">
          <BackButton label="Back" onClick={handleBack} />

          <div>
            <h1>Edit Question</h1>
            <p className="mb-0 text-muted">Question ID: {questionId}</p>
          </div>

          <SaveButton
            label={saving ? "Saving..." : "Save Changes"}
            onClick={handleSave}
            disabled={saving || (submission && uploadingAnswerFile)}
          />
        </div>

        <div className="question-create-layout row g-3">
          <div className="col-12">
            <div className="question-main-form">
              <div className="question-editor-section">
                <h2>Guidance</h2>
                <RichQuestionEditor
                  editorState={guidanceEditorState}
                  onEditorStateChange={setGuidanceEditorState}
                />
              </div>

              {question?.passage_id && (
                <>
                  <div className="question-editor-section">
                    <h2>Passage Type</h2>

                    <select
                      className="question-passage-select"
                      value={selectedPassageTypeId}
                      onChange={(event) => {
                        const passageTypeId = event.target.value;

                        setSelectedPassageTypeId(passageTypeId);

                        // Clear only the selected title and displayed content.
                        setSelectedPassageId("");
                        setSelectedPassageDetails(null);
                        setPassageEditorState(EditorState.createEmpty());
                        setSelectedPassageError("");
                      }}
                    >
                      <option value="">Nothing selected</option>

                      {passageTypes.map((type) => (
                        <option
                          key={type.passage_type_id}
                          value={type.passage_type_id}
                        >
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedPassageTypeId && (
                    <div className="question-editor-section">
                      <h2>Passage Title</h2>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <select
                          className="question-passage-select"
                          value={selectedPassageId}
                          onChange={async (event) => {
                            const passageId = event.target.value;

                            setSelectedPassageId(passageId);
                            setSelectedPassageError("");

                            if (!passageId) {
                              setSelectedPassageDetails(null);
                              setPassageEditorState(EditorState.createEmpty());
                              return;
                            }

                            await loadSelectedPassage(passageId);
                          }}
                        >
                          <option value="">Nothing selected</option>

                          {passages
                            .filter(
                              (passage) =>
                                String(passage.passage_type_id) ===
                                String(selectedPassageTypeId)
                            )
                            .map((passage) => (
                              <option
                                key={passage.passage_id}
                                value={passage.passage_id}
                              >
                                {passage.title}
                              </option>
                            ))}
                        </select>

                        {selectedPassageId && selectedPassageDetails && (
                          <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={() => setPassageModalOpen(true)}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedPassageLoading && (
                    <div className="question-editor-section">
                      <p className="text-muted mb-0">
                        Loading passage...
                      </p>
                    </div>
                  )}

                  {selectedPassageError && (
                    <div className="question-editor-section">
                      <p className="text-danger mb-0">
                        {selectedPassageError}
                      </p>
                    </div>
                  )}

                  {!selectedPassageLoading &&
                    !selectedPassageError &&
                    selectedPassageDetails && (
                      <div className="question-editor-section">
                        <h2>Passage Content</h2>

                        <RichQuestionEditor
                          readOnly
                          editorState={passageEditorState}
                          onEditorStateChange={() => { }}
                        />
                      </div>
                    )}
                </>
              )}

              <div className="question-editor-section">
                <h2>Question Content</h2>
                <RichQuestionEditor
                  editorState={stemEditorState}
                  onEditorStateChange={setStemEditorState}
                />
              </div>

              {subQuestions.length > 0 && (
                <div className="question-editor-section">
                  <h2>Sub Questions</h2>

                  {subQuestions.map((subQuestion, index) => (
                    <div className="question-option-block mb-3" key={subQuestion.id ?? index}>
                      <div className="question-option-block__head">
                        <div>
                          <h3>Sub Question {index + 1}</h3>
                          {/* <label className="question-correct-check">
                            <input
                              type="number"
                              min="0"
                              value={subQuestion.markAllocation}
                              onChange={(event) =>
                                updateSubQuestionField(index, "markAllocation", event.target.value)
                              }
                            />
                            <span>Marks</span>
                          </label> */}
                        </div>
                      </div>

                      <div className="mb-3">
                        <h4 className="mb-2">Content</h4>
                        <RichQuestionEditor
                          editorState={subQuestion.contentEditorState}
                          onEditorStateChange={(editorState) =>
                            updateSubQuestionField(index, "contentEditorState", editorState)
                          }
                        />
                      </div>

                      <div>
                        <h4 className="mb-2">Answer</h4>
                        <RichQuestionEditor
                          editorState={subQuestion.answerEditorState}
                          onEditorStateChange={(editorState) =>
                            updateSubQuestionField(index, "answerEditorState", editorState)
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {mcq ? (
                <div className="question-editor-section">
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                    <h2 className="mb-0">Answer Options</h2>
                    <button
                      type="button"
                      className="question-library-action"
                      onClick={addOption}
                    >
                      Add Option
                    </button>
                  </div>

                  {optionEditorStates.map((editorState, index) => (
                    <div className="question-option-block mb-3" key={`option-${index}`}>
                      <div className="question-option-block__head">
                        <div>
                          <h3>Choice {index + 1}</h3>
                          <label className="question-correct-check">
                            <input
                              type="checkbox"
                              checked={Number(optionMarks[index] ?? 0) > 0}
                              onChange={() => updateOptionMark(index)}
                            />
                            <span>Correct Answer</span>
                          </label>
                        </div>

                        {optionEditorStates.length > 1 && (
                          <button
                            type="button"
                            className="question-library-action question-library-action--danger"
                            onClick={() => removeOption(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <RichQuestionEditor
                        editorState={editorState}
                        onEditorStateChange={(nextEditorState) => updateOptionEditor(index, nextEditorState)}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div>

                  {submission ? (
                    <div className="question-editor-section">
                      <h2>Attached Answer Files</h2>

                      <input
                        ref={submissionFileInputRef}
                        id="submission-answer-file-input"
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        disabled={uploadingAnswerFile}
                        onChange={handleSubmissionFileUpload}
                      />

                      <div
                        className={`submission-dropbox ${uploadingAnswerFile ? "submission-dropbox--disabled" : ""}`}
                        onClick={() => {
                          if (uploadingAnswerFile) return;

                          if (submissionFileInputRef.current) {
                            submissionFileInputRef.current.click();
                          }
                        }}
                      >
                        <strong>
                          {uploadingAnswerFile
                            ? "Uploading..."
                            : attachmentFiles.length > 0
                              ? "Click to add another file"
                              : "Drop files here or click to upload"}
                        </strong>

                        <p className="mb-3 text-muted">
                          These files will be saved as the model answer for this submission question.
                        </p>

                        <div className="submission-dropbox-files">
                          {attachmentFiles.length > 0 ? (
                            attachmentFiles.map((fileName) => (
                              <div
                                key={fileName}
                                className="uploaded-file-card d-flex align-items-center mb-2"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <div className="file-meta flex-grow-1">
                                  <button
                                    type="button"
                                    className="file-title file-title--button"
                                    onClick={() => handleFileDownload(fileName)}
                                  >
                                    {fileName}
                                  </button>

                                  <span className="file-subtitle">Attached answer file</span>
                                </div>

                                <button
                                  type="button"
                                  className="exam-question-tool-btn"
                                  onClick={() => handleSubmissionFileRemove(fileName)}
                                  disabled={uploadingAnswerFile}
                                  title="Remove attachment"
                                >
                                  ×
                                </button>
                              </div>
                            ))
                          ) : (
                            <div className="submission-dropbox-empty">
                              No attachment available.
                            </div>
                          )}
                          </div>
                        </div>
                      </div>
                    ) : subQuestionType ? null : (
                      <div className="question-editor-section">
                        <h2>Model Answer</h2>

                        <RichQuestionEditor
                          editorState={answerEditorState}
                          onEditorStateChange={setAnswerEditorState}
                        />
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>

          {/* Comment Question Type and Allocated Marks from paper review */}
          {/* <div className="col-12 col-xl-4">
            <div className="question-main-form">
              <label className="question-side-field">
                <span>Question Type</span>
                <input value={questionTypeLabel} readOnly />
              </label>

              <label className="question-side-field">
                <span>Allocated Marks</span>
                <input
                  type="number"
                  min="1"
                  value={markAllocation}
                  onChange={(event) => setMarkAllocation(event.target.value)}
                />
              </label> */}

          {/* {question?.subquestions ? (
                <div className="alert alert-info mb-0">
                  This question already has sub-questions. The update payload keeps <strong>subQuestions</strong> set to null for this edit flow.
                </div>
              ) : null} */}
          {/* </div>
          </div> */}
        </div>
        <PassageModal
          show={passageModalOpen}
          mode="edit"
          passage={selectedPassageDetails}
          passageTypeId={selectedPassageDetails?.passage_type_id}
          onClose={() => setPassageModalOpen(false)}
          onSubmit={async (payload) => {
            if (!selectedPassageId) {
              throw new Error("Passage id is missing.");
            }

            const result = await passageService.updatePassage(
              payload,
              selectedPassageId
            );

            if (!result?.success) {
              throw new Error(
                result?.message || "Failed to update passage."
              );
            }

            await loadSelectedPassage(selectedPassageId);

            setPassageModalOpen(false);

            if (window.Swal) {
              await window.Swal.fire({
                title: "Success!",
                text: "Passage updated successfully.",
                icon: "success",
              });
            }

            return result;
          }}
        />
      </section>
    </ContentWrapper>
  );
}
